import { useState, useEffect, createContext, useContext } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth } from '../config/firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  const loadUserProfile = async (userId, userEmail) => {
    try {
      // TODO: Load from Firestore user profile
      // For now, using dummy data
      const isAdminUser = userEmail === 'admin@escape.com';
      const profile = {
        accessLevel: isAdminUser ? 'advanced' : 'basic',
        isAdmin: isAdminUser,
        hasAdvancedAccess: isAdminUser,
      };
      
      setUserProfile(profile);
      console.log('👤 User profile loaded:', profile);
      return profile;
    } catch (error) {
      console.error('Failed to load user profile:', error);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await loadUserProfile(user.uid, user.email);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signup = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateUserAccess = async (accessData) => {
    if (userProfile) {
      const updatedProfile = {
        ...userProfile,
        ...accessData
      };
      setUserProfile(updatedProfile);
      console.log('🔓 User access updated:', updatedProfile);
    }
  };

  const value = {
    user,
    userProfile,
    signup,
    login,
    logout,
    loading,
    loadUserProfile,
    updateUserAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
